import type {
  CycleTimeHistogramBucket,
  MeasureValues,
} from '../../../../api/agent-miner-client/agent-miner-client.type';
import type { TimeAllocation, TimeAllocationType } from '../../../../api/work-periods-client/work-periods-client.type';
import { getTimeAllocationChartLabels } from '../../components/line-chart/line-chart.helpers';
import type { ChartDataItem } from '../../components/line-chart/line-chart.type';

const HISTOGRAM_BUCKET_SIZE = 5;

/**
 * Sums all non-null values in a measure data record.
 *
 * Args:
 *   measureData: MeasureValues | undefined - The measure data to sum
 * Returns:
 *   number - The sum of all non-null values
 */
const sumMeasureValues = (measureData: MeasureValues | undefined): number => {
  if (!measureData) return 0;
  return Object.values(measureData).reduce<number>((acc, value) => acc + (value ?? 0), 0);
};

/**
 * Gets the last non-null value from a measure data record, sorted chronologically by key.
 *
 * Args:
 *   measureData: MeasureValues | undefined - The measure data to search
 * Returns:
 *   number - The last non-null value, or 0 if none found
 */
const getLastNonNullValue = (measureData: MeasureValues | undefined): number => {
  if (!measureData) return 0;
  const sortedKeys = Object.keys(measureData).sort();
  for (let index = sortedKeys.length - 1; index >= 0; index--) {
    const value = measureData[sortedKeys[index]];
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return 0;
};

/**
 * Transforms measure data records into chart data items sorted by date, with time-allocation-aware
 * labels: the last weekly/biweekly bucket tick shows the bucket end date (the user-selected end date)
 * and tooltips show the full bucket window (e.g. "May 25 – May 31").
 *
 * @param {Record<string, MeasureValues | undefined>} measures - Map of dataKey to measure data
 * @param {TimeAllocationType} timeAllocationType - The active time allocation granularity
 * @param {TimeAllocation[]} timeAllocations - Time allocation buckets for the selected date range
 * @returns {ChartDataItem[]} The transformed chart data
 */
const buildChartData = ({
  measures,
  timeAllocationType,
  timeAllocations,
}: {
  measures: Record<string, MeasureValues | undefined>;
  timeAllocationType: TimeAllocationType;
  timeAllocations: TimeAllocation[];
}): ChartDataItem[] => {
  const allDates = new Set<string>();

  for (const measureData of Object.values(measures)) {
    if (measureData) {
      for (const key of Object.keys(measureData)) {
        allDates.add(key);
      }
    }
  }

  return Array.from(allDates)
    .sort()
    .map((dateKey) => {
      const { tickLabel, tooltipLabel } = getTimeAllocationChartLabels({
        rawDate: dateKey,
        timeAllocationType,
        timeAllocations,
      });

      const dataPoint: ChartDataItem = {
        date: tickLabel,
        rawDate: dateKey,
        dateRange: tooltipLabel,
      };
      for (const [dataKey, measureData] of Object.entries(measures)) {
        dataPoint[dataKey] = measureData?.[dateKey] ?? null;
      }
      return dataPoint;
    });
};

/**
 * Finds the bucket label that contains a given median value.
 *
 * Args:
 *   median: number - The median cycle time in days
 *   histogram: CycleTimeHistogramBucket[] - The histogram buckets
 * Returns:
 *   string | null - The bucket_days label containing the median, or null
 */
const findBucketForMedian = (median: number, histogram: CycleTimeHistogramBucket[]): string | null => {
  const bucketIndex = Math.min(Math.floor(median / HISTOGRAM_BUCKET_SIZE), histogram.length - 1);
  return histogram[bucketIndex]?.bucket_days ?? null;
};

export { buildChartData, findBucketForMedian, getLastNonNullValue, sumMeasureValues };
